import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import * as Yup from "yup"

import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"
import SweetAlerts from "../../../components/SweetAlerts"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { vehicleType } from "../../../lib/GlobalVariables"

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
]

const verificationStatusOptions = [
  { value: "VERIFIED", label: "VERIFIED" },
  { value: "NOT_VERIFIED", label: "NOT VERIFIED" },
]

const AddEditFleetVehicle = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [fleetClientData, setFleetClientData] = useState([])
  const [brandData, setBrandData] = useState([])
  const [connectorData, setConnectorData] = useState([])
  const [showReleaseAlert, setShowReleaseAlert] = useState(false)

  const validationSchema = Yup.object({
    fleet_client_ids: Yup.array()
      .length(1, "Please select exactly one fleet client")
      .required("Fleet clients are required"),
    registration_number: Yup.string()
      .trim()
      .min(2, "Enter at least 2 characters for registration number")
      .matches(
        /^[A-Z0-9]+$/,
        "Registration number must contain only capital letters and numbers",
      )
      .required("Registration number is required"),
    brand_id: Yup.mixed()
      .test("required", "Brand is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Brand is required"),
    model: Yup.string()
      .trim()
      .min(2, "Enter at least 2 characters for model")
      .required("Model is required"),
    manufacturing_year: Yup.mixed()
      .test("required", "Manufacturing year is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Manufacturing year is required"),
    vehicle_type: Yup.mixed()
      .test("required", "Vehicle type is required", (value) => {
        const normalizedValue = value?.value ?? value
        return !!normalizedValue || normalizedValue === 0
      })
      .required("Vehicle type is required"),
    battery_capacity: Yup.number()
      .typeError("Battery capacity is required")
      .min(1, "Battery capacity must be greater than 0")
      .required("Battery capacity is required"),
    battery_voltage: Yup.number()
      .typeError("Battery voltage is required")
      .min(1, "Battery voltage must be greater than 0")
      .required("Battery voltage is required"),
    connector_ids: Yup.array()
      .min(1, "Please provide at least one connector")
      .required("At least one connector is required"),
    ...(isUpdate
      ? {
          status: Yup.string().required("Status is required"),
          verification_status: Yup.string().required(
            "Verification status is required",
          ),
        }
      : {}),
  })

  const fetchFleetClientData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.fleetClientList}?page=1&limit=200`,
      })

      if (response?.data?.code === 200) {
        const clients = (response?.data?.data || []).map((client) => ({
          value: client?.id,
          label: client?.name,
        }))

        const updatedClients = clients.filter(
          (client) => client.value !== undefined && client.label !== undefined,
        )

        setFleetClientData(updatedClients)
      }
    } catch (error) {
      toast.error(error?.message || "Unable to fetch fleet clients")
    }
  }

  const fetchBrandData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.brandList,
      })

      if (response?.data?.code === 200) {
        const brands = (response?.data?.data || []).map((brand) => ({
          value: brand?.id,
          label: brand?.brand_name,
        }))

        setBrandData(brands)
      }
    } catch (error) {
      toast.error(error?.message || "Unable to fetch brands")
    }
  }

  const fetchConnectorData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.connectorList,
      })

      if (response?.data?.code === 200) {
        const connectors = (response?.data?.data || []).map((connector) => ({
          value: connector?.id,
          label: connector?.connector_type,
        }))

        setConnectorData(connectors)
      }
    } catch (error) {
      toast.error(error?.message || "Unable to fetch connectors")
    }
  }

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)

      const payload = {
        fleet_client_ids: (values?.fleet_client_ids || []).map((client) =>
          client?.value ?? client,
        ),
        registration_number: values?.registration_number?.trim(),
        brand_id: Number(values?.brand_id?.value ?? values?.brand_id),
        model: values?.model?.trim(),
        manufacturing_year: Number(
          values?.manufacturing_year?.value ?? values?.manufacturing_year,
        ),
        vehicle_type:
          values?.vehicle_type?.label ??
          values?.vehicle_type?.value ??
          values?.vehicle_type,
        battery_capacity: Number(values?.battery_capacity),
        battery_voltage: Number(values?.battery_voltage),
        connector_ids: (values?.connector_ids || []).map((connector) =>
          Number(connector?.value ?? connector),
        ),
        ...(isUpdate
          ? {
              status: values?.status || "ACTIVE",
              verification_status:
                values?.verification_status || "NOT_VERIFIED",
            }
          : {}),
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateFleetVehicle + isUpdate.id
          : admin.createFleetVehicle,
        data: payload,
      })

      if (response?.data?.code === 200 || response?.data?.code === 201) {
        toast.success(response?.data?.message)

        setShow(false)
        setIsUpdate(false)
        resetForm()
        fetchData()
      } else {
        throw new Error(
          response?.data?.message || "Unable to save Fleet Vehicle",
        )
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const handleRelease = async () => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateFleetVehicle + isUpdate.id,
        data: { verification_status: "NOT_VERIFIED" },
      })

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message)
        setShowReleaseAlert(false)
        setShow(false)
        setIsUpdate(false)
        fetchData()
      } else {
        throw new Error(
          response?.data?.message || "Unable to release Fleet Vehicle",
        )
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          fleet_client_ids:
            isUpdate?.fleet_clients?.map((client) => ({
              value: client?.id,
              label: client?.name,
            })) || [],
          registration_number: isUpdate?.registration_number || "",
          brand_id:
            isUpdate?.brand_id !== undefined && isUpdate?.brand
              ? {
                  value: isUpdate.brand_id,
                  label: isUpdate.brand.brand_name,
                }
              : "",
          model: isUpdate?.model || "",
          manufacturing_year: isUpdate?.manufacturing_year || "",
          vehicle_type:
            vehicleType.find(
              (type) =>
                String(type.value) === String(isUpdate?.vehicle_type) ||
                String(type.label) === String(isUpdate?.vehicle_type),
            ) || "",
          battery_capacity: isUpdate?.battery_capacity || "",
          battery_voltage: isUpdate?.battery_voltage || "",
          connector_ids:
            isUpdate?.connectors?.map((connector) => ({
              value: connector?.id,
              label: connector?.connector_type,
            })) || [],
          status: isUpdate?.status || "ACTIVE",
          verification_status: isUpdate?.verification_status || "NOT_VERIFIED",
        }
      : {
          fleet_client_ids: [],
          registration_number: "",
          brand_id: "",
          model: "",
          manufacturing_year: "",
          vehicle_type: "",
          battery_capacity: "",
          battery_voltage: "",
          connector_ids: [],
          status: "ACTIVE",
          verification_status: "NOT_VERIFIED",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { values, errors, touched, setFieldValue, resetForm } = formik

  useEffect(() => {
    if (show) {
      fetchFleetClientData()
      fetchBrandData()
      fetchConnectorData()
    }
  }, [show])

  const currentYear = new Date().getFullYear()
  const manufacturingYears = Array.from({ length: 30 }, (_, index) => {
    const year = currentYear - index

    return {
      value: year,
      label: String(year),
    }
  })

  return (
    <>
      <ModalComponent
        show={show}
        modalsize="lg"
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Fleet Vehicle" : "Add Fleet Vehicle"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext="Cancel"
        handleSubmit={formik.handleSubmit}
        children={
        <Form noValidate>
          <Row xs="auto">
            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Fleet Client <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  fleetClientData.find(
                    (client) =>
                      String(client.value) ===
                      String(
                        values?.fleet_client_ids?.[0]?.value ??
                          values?.fleet_client_ids?.[0],
                      ),
                  ) || null
                }
                handleSelectChange={(selectedOptions) => {
                  setFieldValue(
                    "fleet_client_ids",
                    selectedOptions
                      ? [
                          {
                            value: selectedOptions.value,
                            label: selectedOptions.label,
                          },
                        ]
                      : [],
                  )
                }}
                options={fleetClientData}
                isMulti={false}
                placeholder="Select Fleet Client"
              />

              {touched?.fleet_client_ids && errors?.fleet_client_ids && (
                <p className="invalid_error">{errors.fleet_client_ids}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="registrationNumber"
                label={
                  <>
                    Registration Number <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="registration_number"
                  autoComplete="false"
                  maxLength={50}
                  value={values?.registration_number}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "registration_number",
                      e.target.value.toUpperCase(),
                    )
                  }
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.registration_number && errors?.registration_number
                  }
                  isValid={
                    touched?.registration_number && !errors?.registration_number
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.registration_number}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Brand <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  brandData.find(
                    (brand) => brand.value === values?.brand_id?.value,
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue("brand_id", selectedOption || "")
                }}
                options={brandData}
                placeholder="Select Brand"
              />

              {touched?.brand_id && errors?.brand_id && (
                <p className="invalid_error">{errors.brand_id}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="fleetVehicleModel"
                label={
                  <>
                    Model <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="model"
                  autoComplete="false"
                  maxLength={100}
                  value={values?.model}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={touched?.model && errors?.model}
                  isValid={touched?.model && !errors?.model}
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.model}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Manufacturing Year <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  manufacturingYears.find(
                    (year) =>
                      Number(year.value) === Number(values?.manufacturing_year),
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue(
                    "manufacturing_year",
                    selectedOption ? selectedOption.value : "",
                  )
                }}
                options={manufacturingYears}
                placeholder="Select Manufacturing Year"
              />

              {touched?.manufacturing_year && errors?.manufacturing_year && (
                <p className="invalid_error">{errors.manufacturing_year}</p>
              )}
            </Col>

            <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Vehicle Type <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  vehicleType.find(
                    (type) =>
                      String(type.value) ===
                        String(
                          values?.vehicle_type?.value ?? values?.vehicle_type,
                        ) ||
                      String(type.label) ===
                        String(
                          values?.vehicle_type?.label ?? values?.vehicle_type,
                        ),
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue("vehicle_type", selectedOption || "")
                }}
                options={vehicleType}
                placeholder="Select Vehicle Type"
              />

              {touched?.vehicle_type && errors?.vehicle_type && (
                <p className="invalid_error">{errors.vehicle_type}</p>
              )}
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="batteryCapacity"
                label={
                  <>
                    Battery Capacity <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="number"
                  placeholder=""
                  name="battery_capacity"
                  value={values?.battery_capacity}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.battery_capacity && errors?.battery_capacity
                  }
                  isValid={
                    touched?.battery_capacity && !errors?.battery_capacity
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.battery_capacity}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} md={6}>
              <FloatingLabel
                controlId="batteryVoltage"
                label={
                  <>
                    Battery Voltage <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="number"
                  placeholder=""
                  name="battery_voltage"
                  value={values?.battery_voltage}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={
                    touched?.battery_voltage && errors?.battery_voltage
                  }
                  isValid={touched?.battery_voltage && !errors?.battery_voltage}
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.battery_voltage}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Connectors <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={connectorData.filter((connector) =>
                  (values?.connector_ids || []).some(
                    (selectedConnector) =>
                      Number(selectedConnector?.value ?? selectedConnector) ===
                      Number(connector.value),
                  ),
                )}
                handleSelectChange={(selectedOptions) => {
                  setFieldValue(
                    "connector_ids",
                    selectedOptions
                      ? selectedOptions.map((option) => ({
                          value: option?.value,
                          label: option?.label,
                        }))
                      : [],
                  )
                }}
                options={connectorData}
                isMulti={true}
                placeholder="Select Connectors"
              />

              {touched?.connector_ids && errors?.connector_ids && (
                <p className="invalid_error">{errors.connector_ids}</p>
              )}
            </Col>

            {isUpdate && (
              <>
                <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
                  <label>
                    Status <span className="required_star">*</span>
                  </label>

                  <CustomSelect
                    value={
                      statusOptions.find(
                        (status) => status.value === values?.status,
                      ) || null
                    }
                    handleSelectChange={(selectedOption) => {
                      setFieldValue("status", selectedOption?.value || "ACTIVE")
                    }}
                    options={statusOptions}
                    placeholder="Select Status"
                  />

                  {touched?.status && errors?.status && (
                    <p className="invalid_error">{errors.status}</p>
                  )}
                </Col>

                <Col xs={12} md={6} className="mb-3 custom_selectinvalid_bdr">
                  <label>
                    Verification Status <span className="required_star">*</span>
                  </label>

                  <CustomSelect
                    value={
                      verificationStatusOptions.find(
                        (status) =>
                          status.value === values?.verification_status,
                      ) || null
                    }
                    handleSelectChange={(selectedOption) => {
                      if (
                        values?.verification_status === "VERIFIED" &&
                        selectedOption?.value === "NOT_VERIFIED"
                      ) {
                        setShowReleaseAlert(true)
                        return
                      }

                      setFieldValue(
                        "verification_status",
                        selectedOption?.value || "NOT_VERIFIED",
                      )
                    }}
                    options={verificationStatusOptions}
                    placeholder="Select Verification Status"
                  />

                  {touched?.verification_status &&
                    errors?.verification_status && (
                      <p className="invalid_error">
                        {errors.verification_status}
                      </p>
                    )}
                </Col>
              </>
            )}
          </Row>
        </Form>
        }
      />

      <SweetAlerts
        showAlert={showReleaseAlert}
        loading={loading}
        alertMessage="Are you sure you want to release this fleet vehicle? It will become available for another driver to claim."
        handleClose={() => setShowReleaseAlert(false)}
        handleAlert={handleRelease}
      />
    </>
  )
}

export default AddEditFleetVehicle
